import React from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import { motion } from "motion/react";
import { FileDown, Users, BookOpen, Globe2, Music, Calendar, ArrowRight } from "lucide-react";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import apiClient from '../../api/axios';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

export const Home: React.FC = () => {
    const navigate = useNavigate();
    
    // Fetch page metadata and update title
    usePageMetadata('home');


    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 },
    };

    // Fetch Active Posters/Events
    const { data: posters, isLoading: loadingPosters } = useQuery({
        queryKey: ['publicPosters', 3],
        queryFn: async () => {
            const response = await apiClient.get('/public/posters?pageSize=3');
            return response.data.data || response.data;
        }
    });

    const { data: homeConfig, isLoading: loadingConfig } = useQuery({
        queryKey: ['publicHomePage'],
        queryFn: async () => {
            const response = await apiClient.get('/public/homepage');
            return response.data.data;
        }
    });

    // Default dictionary if fetch fails
    const t = homeConfig?.dictionary || {
        t_HeroFallbackTitle: "Vielfalt ist unsere Stärke, Bildung der Schlüssel.",
        t_HeroFallbackSubtitle: "Seit 2009 engagiert sich der Mosaik e.V. in Offenbach für Soziokultur, Integration und Chancengleichheit in der Bildung. Entdecken Sie unsere Projekte, Kurse und Veranstaltungen.",
        t_HeroFallbackButton1Text: "Unsere Projekte",
        t_HeroFallbackButton2Text: "Zum Kursangebot",
        t_EventsTitle: "Aktuelle Veranstaltungen",
        t_EventsSubtitle: "Entdecken Sie kommende Highlights und Events in unserem Verein.",
        t_EventsViewAllText: "Alle ansehen",
        t_EventsEmptyText: "Zur Zeit gibt es keine anstehenden Veranstaltungen.",
        t_EventDetailsText: "Details ansehen",
        t_PillarsTitle: "Unsere 4 Säulen",
        t_PillarsSubtitle: "Der Mosaik e.V. stützt sich in seiner Arbeit auf vier fundamentale Handlungsfelder.",
        t_Pillar1Title: "Kinder- und Jugendarbeit",
        t_Pillar1Desc: "Förderung von Potenzialen, Nachhilfe und Mentoring für die jüngere Generation.",
        t_Pillar2Title: "Erwachsenenbildung",
        t_Pillar2Desc: "Seminare, Sprachförderung und Elternarbeit zur Stärkung der gesellschaftlichen Teilhabe.",
        t_Pillar3Title: "Gesellschaft und Umwelt",
        t_Pillar3Desc: "Projekte zu Inklusion, Demokratieförderung und ökologischem Bewusstsein.",
        t_Pillar4Title: "Kultur, Kunst und Musik",
        t_Pillar4Desc: "Kreative Entfaltung durch Kalligraphie, Ebru-Kunst und musikalische Angebote.",
        t_NewsletterTitle: "Bleiben Sie auf dem Laufenden",
        t_NewsletterSubtitle: "Laden Sie sich unsere aktuellen Newsletter herunter und erfahren Sie mehr über die neuesten Projekte und Entwicklungen im Verein.",
        t_NewsletterButton1Text: "1. Halbjährlicher Newsletter 2024",
        t_NewsletterButton1Url: "#",
        t_NewsletterButton2Text: "2. Halbjährlicher Newsletter 2024",
        t_NewsletterButton2Url: "#"
    };

    // Pillars Data
    const pillars = [
        {
            title: t.t_Pillar1Title,
            description: t.t_Pillar1Desc,
            icon: Users,
        },
        {
            title: t.t_Pillar2Title,
            description: t.t_Pillar2Desc,
            icon: BookOpen,
        },
        {
            title: t.t_Pillar3Title,
            description: t.t_Pillar3Desc,
            icon: Globe2,
        },
        {
            title: t.t_Pillar4Title,
            description: t.t_Pillar4Desc,
            icon: Music,
        },
    ];

    if (loadingConfig) {
        return (
            <div className="flex justify-center items-center py-40">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            {/* Hero Section / Swiper Dynamic Slider */}
            {homeConfig?.heroSlider ? (
                <section className="relative w-full h-[600px] lg:h-[700px] bg-slate-900">
                    <Swiper
                        modules={[Autoplay, Pagination, Navigation, EffectFade]}
                        effect="fade"
                        speed={1000}
                        autoplay={homeConfig.heroSlider.autoplaySpeed > 0 ? {
                            delay: homeConfig.heroSlider.autoplaySpeed,
                            disableOnInteraction: false,
                        } : false}
                        pagination={homeConfig.heroSlider.showDots ? { clickable: true } : false}
                        navigation={homeConfig.heroSlider.showArrows}
                        className="w-full h-full"
                    >
                        {homeConfig.heroSlider.items?.map((slide: any) => {
                            const hasText = Boolean(
                                slide.title?.trim() ||
                                slide.subtitle?.trim() ||
                                slide.buttonText?.trim()
                            );

                            return (
                                <SwiperSlide key={slide.id} className="relative w-full h-full">
                                    <div className="absolute inset-0">
                                        <img
                                            src={resolveAssetUrl(slide.imageUrl)}
                                            alt={slide.title || "Slider Image"}
                                            className="w-full h-full object-cover"
                                        />
                                        {hasText && <div className="absolute inset-0 bg-black/40" />}
                                    </div>
                                    {hasText ? (
                                        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center items-center text-center z-10">
                                            <motion.div
                                                initial={{ opacity: 0, y: 30 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.8 }}
                                                className="max-w-3xl"
                                            >
                                                {slide.title && (
                                                    <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl drop-shadow-lg">
                                                        {slide.title}
                                                    </h1>
                                                )}
                                                {slide.subtitle && (
                                                    <p className="mt-6 text-lg xl:text-xl leading-8 text-gray-200 drop-shadow-md">
                                                        {slide.subtitle}
                                                    </p>
                                                )}
                                                {slide.buttonText && slide.buttonUrl && (
                                                    <div className="mt-10 flex items-center justify-center">
                                                        {slide.buttonUrl.startsWith('http') ? (
                                                            <a
                                                                href={slide.buttonUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="rounded-xl bg-accent px-8 py-3.5 text-base font-semibold text-accent-foreground shadow-sm hover:bg-accent/90 transition-all hover:-translate-y-1"
                                                            >
                                                                {slide.buttonText}
                                                            </a>
                                                        ) : (
                                                            <Link
                                                                to={slide.buttonUrl}
                                                                className="rounded-xl bg-accent px-8 py-3.5 text-base font-semibold text-accent-foreground shadow-sm hover:bg-accent/90 transition-all hover:-translate-y-1"
                                                            >
                                                                {slide.buttonText}
                                                            </Link>
                                                        )}
                                                    </div>
                                                )}
                                            </motion.div>
                                        </div>
                                    ) : slide.buttonUrl && (
                                        slide.buttonUrl.startsWith('http') ? (
                                            <a
                                                href={slide.buttonUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="absolute inset-0 z-10"
                                                aria-label={slide.title || "Slider Link"}
                                            />
                                        ) : (
                                            <Link
                                                to={slide.buttonUrl}
                                                className="absolute inset-0 z-10"
                                                aria-label={slide.title || "Slider Link"}
                                            />
                                        )
                                    )}
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </section>
            ) : (
                <section className="relative overflow-hidden bg-[#0d6efd] py-24 sm:py-32 lg:pb-40">
                    <div className="absolute inset-0" style={{ 
                        backgroundImage: "linear-gradient(to right, rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.05) 1px, transparent 1px)", 
                        backgroundSize: "40px 40px" 
                    }} />
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="mx-auto max-w-4xl"
                        >
                            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl drop-shadow-sm">
                                {t.t_HeroFallbackTitle}
                            </h1>
                            <p className="mt-6 mx-auto max-w-2xl text-sm sm:text-base leading-7 text-white/95">
                                {t.t_HeroFallbackSubtitle}
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                                <Link
                                    to="/projekte"
                                    className="rounded-lg bg-accent px-8 py-3 text-sm font-bold text-white shadow-md hover:bg-accent/90 transition-all hover:-translate-y-0.5"
                                >
                                    {t.t_HeroFallbackButton1Text}
                                </Link>
                                <Link
                                    to="/kursangebot"
                                    className="rounded-lg bg-accent px-8 py-3 text-sm font-bold text-white shadow-md hover:bg-accent/90 transition-colors flex items-center gap-2 hover:-translate-y-0.5"
                                >
                                    {t.t_HeroFallbackButton2Text} <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </section>
            )}

            {/* Aktuelle Veranstaltungen (Dynamic API Data) */}
            <section className="py-24 sm:py-32 bg-background">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
                        <div>
                            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl flex items-center gap-3">
                                <Calendar className="h-8 w-8 text-primary" />
                                {t.t_EventsTitle}
                            </h2>
                            <p className="mt-2 text-lg text-foreground/70">
                                {t.t_EventsSubtitle}
                            </p>
                        </div>
                        <Link
                            to="/veranstaltungen"
                            className="text-primary hover:text-primary/80 font-semibold flex items-center gap-1 transition-colors"
                        >
                            {t.t_EventsViewAllText} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {loadingPosters ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                        </div>
                    ) : posters && posters.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {posters.map((poster: any) => (
                                <div
                                    key={poster.id}
                                    className="group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-border transition-all hover:shadow-md cursor-pointer"
                                    onClick={() => poster.isRedirect ? window.open(poster.pageUrl, '_blank') : navigate(`/veranstaltungen/${poster.pageUrl}`)}
                                >
                                    <div className="aspect-w-16 aspect-h-9 relative h-56 overflow-hidden bg-gray-100">
                                        <img
                                            src={resolveAssetUrl(poster.imageUrl) || 'https://via.placeholder.com/400x250?text=Event'}
                                            alt={poster.title}
                                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="flex flex-1 flex-col p-6">
                                        <h3 className="text-xl font-semibold text-foreground line-clamp-2">
                                            {poster.title}
                                        </h3>
                                        <p className="mt-3 text-base text-foreground/70 line-clamp-3">
                                            {poster.description}
                                        </p>
                                        <div className="mt-auto pt-6">
                                            <span className="inline-flex items-center text-sm font-semibold text-primary">
                                                {t.t_EventDetailsText} <ArrowRight className="ml-1 h-4 w-4" />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center text-foreground/70">
                            {t.t_EventsEmptyText}
                        </div>
                    )}
                </div>
            </section>

            {/* 4 Pillars Section */}
            <section className="py-24 sm:py-32 bg-secondary/50">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                            {t.t_PillarsTitle}
                        </h2>
                        <p className="mt-4 text-lg leading-8 text-foreground/70">
                            {t.t_PillarsSubtitle}
                        </p>
                    </div>

                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        className="mx-auto mt-16 max-w-7xl grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4"
                    >
                        {pillars.map((pillar) => {
                            const Icon = pillar.icon;
                            return (
                                <motion.div
                                    key={pillar.title}
                                    variants={itemVariants}
                                    className="rounded-2xl border border-border bg-background p-8 shadow-sm transition-shadow hover:shadow-md"
                                >
                                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                                        <Icon className="h-6 w-6 text-primary" />
                                    </div>
                                    <h3 className="mt-6 text-lg font-semibold leading-8 text-foreground">
                                        {pillar.title}
                                    </h3>
                                    <p className="mt-2 text-base leading-7 text-foreground/70">
                                        {pillar.description}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </div>
            </section>

            {/* Downloads / Newsletter Section */}
            <section id="newsletter" className="py-24 sm:py-32 bg-background">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="rounded-3xl bg-secondary px-6 py-16 sm:p-16 relative overflow-hidden flex flex-col items-center text-center">
                        <h2 className="relative z-10 text-2xl font-bold tracking-tight text-foreground sm:text-3xl mb-4">
                            {t.t_NewsletterTitle || "Newsletter"}
                        </h2>
                        <p className="relative z-10 mx-auto max-w-xl text-lg leading-8 text-foreground/80 mb-10">
                            {t.t_NewsletterSubtitle || "Laden Sie sich unsere aktuellen Newsletter herunter und erfahren Sie mehr über die neuesten Projekte und Entwicklungen im Verein."}
                        </p>
                        
                        {Array.isArray(homeConfig?.latestNewsletters) && homeConfig.latestNewsletters.length > 0 ? (
                            <div className="relative z-10 flex flex-wrap gap-4 w-full justify-center max-w-5xl">
                                {homeConfig.latestNewsletters.map((nl: any) => (
                                    <a
                                        key={nl.id}
                                        href={resolveAssetUrl(nl.fileUrl)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-background px-6 py-3.5 text-sm font-semibold text-foreground shadow-sm ring-1 ring-inset ring-border transition-all hover:bg-primary hover:text-primary-foreground hover:scale-105"
                                    >
                                        <FileDown className="h-5 w-5 text-primary" />
                                        <span>{nl.title}</span>
                                    </a>
                                ))}
                            </div>
                        ) : (
                            <div className="relative z-10 flex flex-col sm:flex-row gap-4 w-full sm:w-auto justify-center">
                                <a
                                    href={resolveAssetUrl(t.t_NewsletterButton1Url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-background px-6 py-4 text-sm font-semibold text-foreground shadow-sm ring-1 ring-inset ring-border transition-all hover:bg-secondary-foreground hover:text-background"
                                >
                                    <FileDown className="h-5 w-5" />
                                    {t.t_NewsletterButton1Text}
                                </a>
                                <a
                                    href={resolveAssetUrl(t.t_NewsletterButton2Url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-4 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-primary/90"
                                >
                                    <FileDown className="h-5 w-5" />
                                    {t.t_NewsletterButton2Text}
                                </a>
                            </div>
                        )}

                        <div className="relative z-10 mt-10">
                            <Link
                                to="/newsletter-archiv"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors bg-primary/10 hover:bg-primary/20 px-5 py-2.5 rounded-full"
                            >
                                Newsletter-Archiv ansehen <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};
